import { BaseFeature } from '../../core/base-feature.js';

/**
 * PhantomCursorFeature - Frontend feature for phantom cursor control
 *
 * Sends cursor position and configuration commands to the desktop.
 * moveCursor() is fire-and-forget for performance (~30fps).
 */
export class PhantomCursorFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        this.state = {
            isVisible: false,
            cursorStyle: 'glow',
            trailEnabled: false
        };
    }

    static getMetadata() {
        return {
            id: 'phantom-cursor',
            name: 'Phantom Cursor',
            version: 'v1.0',
            capabilities: [
                'show_cursor',
                'hide_cursor',
                'move_cursor',
                'set_trail',
                'set_style',
                'get_state'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[PhantomCursor] Initializing...');
    }

    /**
     * Show the phantom cursor on desktop
     * @param {string} [style] - Cursor style: 'glow', 'crosshair', 'eye'
     */
    show(style) {
        this.sendMessage('show_cursor', { style: style || this.state.cursorStyle });
        this.state.isVisible = true;
        if (style) this.state.cursorStyle = style;
        this.emit('stateChanged', this.state);
    }

    /**
     * Hide the phantom cursor
     */
    hide() {
        this.sendMessage('hide_cursor', {});
        this.state.isVisible = false;
        this.emit('stateChanged', this.state);
    }

    /**
     * Move the cursor to screen coordinates — fire-and-forget for performance.
     * @param {number} x - Screen X coordinate
     * @param {number} y - Screen Y coordinate
     */
    moveCursor(x, y) {
        this.sendMessage('move_cursor', { x, y });
    }

    /**
     * Enable or disable trail effect
     * @param {boolean} enabled
     */
    setTrail(enabled) {
        this.sendMessage('set_trail', { enabled });
        this.state.trailEnabled = enabled;
        this.emit('stateChanged', this.state);
    }

    /**
     * Set cursor style
     * @param {string} style - 'glow', 'crosshair', 'eye'
     */
    setStyle(style) {
        this.sendMessage('set_style', { style });
        this.state.cursorStyle = style;
        this.emit('stateChanged', this.state);
    }

    /**
     * Get current state from desktop
     */
    getRemoteState() {
        this.sendMessage('get_state', {});
    }

    /**
     * Handle incoming messages from desktop
     */
    async handleMessage(message) {
        const { action, payload } = message;

        switch (action) {
            case 'show_cursor_response':
            case 'hide_cursor_response':
            case 'set_trail_response':
            case 'set_style_response':
                if (payload.success !== undefined) {
                    this.emit('commandResult', { action, ...payload });
                }
                break;

            case 'get_state_response':
                if (payload.success) {
                    this.state.isVisible = payload.isVisible;
                    this.state.cursorStyle = payload.cursorStyle;
                    this.state.trailEnabled = payload.trailEnabled;
                    this.emit('stateChanged', this.state);
                }
                break;

            default:
                console.warn(`[PhantomCursor] Unknown action: ${action}`);
        }
    }

    async cleanup() {
        console.log('[PhantomCursor] Cleaning up...');
        if (this.state.isVisible) {
            this.hide();
        }
    }
}
